import _ from "lodash";
import React, { useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

import { Button2 } from "@/ui2/atoms/Button";
import { calculateAge } from "@/utils/momentUtils";
import { DashboardCard2 } from "@/components/layout/DashboardCard2";
import { Divider2 } from "@/ui2/atoms/Divider";
import { formatMobileNumber } from "@/utils/filters";
import { getNoteMetaContent } from "@/features/member-profile/(notes)/(list)/components/NoteMetaData";
import images from "@/utils/images";
import { MemberHeader } from "@/component-library/MemberHeader";
import { NoteActions } from "@/components/Common/Note/NoteActions";
import { selectProfile } from "@/redux/selectors/authSelectors";
import { usePatientDetails } from "@/features/patient/api/queries/usePatientDetails";
import { usePatientDiagnosis } from "@/features/patient/api/queries/usePatientDiagnosis";
import type { UUID } from "@/core/types";

import { Addendums } from "../(addendum)/Addendums";
import { useDeleteNote } from "../api/mutations/useDeleteNote";
import { useNoteDetails } from "../api/queries/useNoteDetails";

import { AccessingPHIDisclaimer } from "./components/AccessingPHIDisclaimer";
import { CosignatureReviewSection } from "./components/CosignatureReviewSection";
import { CosignRejectedAlert } from "./components/CosignRejectedAlert";
import { DeleteNote } from "./components/DeleteNote";
import { GenericNoteView } from "./GenericNoteView";
import { getNoteBadgesContent } from "./components/NoteBadges";

export function FullNoteView() {
  const { noteId, patientId } = useParams<{ noteId: UUID; patientId: UUID }>();
  const profile = useSelector(selectProfile);
  const history = useHistory();

  const { data: note, refetch: refetchNote } = useNoteDetails({ noteId, patientId });
  const { data: patient } = usePatientDetails({ patientId });
  const { data: diagnosis } = usePatientDiagnosis({ patientId });

  const [showDeleteNotePopUp, setShowDeleteNotePopUp] = useState(false);

  const [draftNoteId, setDraftNoteId] = useState<UUID>(null);
  const [isEditing, setIsEditing] = useState(false);

  const { mutate: deleteNote } = useDeleteNote({
    onSuccess: () => {
      setShowDeleteNotePopUp(false);
      goBack();
    },
  });

  const primaryDiagnosis = _.get(diagnosis, "ICD_10_axis_1_diagnosis", [])
    .map((icd_10: { value: string }) => icd_10["value"])
    .join(", ");

  const secondaryDiagnosis = _.get(diagnosis, "ICD_10_axis_2_diagnosis", [])
    .map((icd_10: { value: string }) => icd_10["value"])
    .join(", ");

  const isNoteEditable = () => {
    if (!note) return false;

    return (
      (note.note_state === "draft" || note.note_state === "rejected") &&
      profile?.username === note.created_by
    );
  };

  const goBack = () => {
    history.push(`/app/patient/${patientId}/profile/v2?selectedTab=Notes`);
  };

  const isEditable = isNoteEditable();
  const isRejected = note?.note_state === "rejected";
  const noteBadges = note ? getNoteBadgesContent({ badgeSize: "md", note }) : null;
  const noteMeta = note ? getNoteMetaContent({ note, showSignature: false }) : null;

  return (
    <>
      <Button2
        className="tw:flex tw:max-w-min tw:gap-2 tw:print:hidden"
        classNameChildren="tw:flex tw:gap-2 tw:text-text-tertiary tw:font-medium"
        icon={<img alt="back" className="tw:max-w-full" src={images("./common/solidBckBtn.svg")} />}
        onClick={goBack}
        variant="blank"
      >
        Back to Patient Profile
      </Button2>

      {showDeleteNotePopUp && (
        <DeleteNote
          clickHandler={() =>
            draftNoteId && deleteNote({ noteId: draftNoteId, patientId: patientId as UUID })
          }
          close={() => setShowDeleteNotePopUp(false)}
        />
      )}

      <DashboardCard2 className="tw:flex tw:p-5!" classNameChildren="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-row tw:justify-between tw:gap-6">
          <AccessingPHIDisclaimer />

          <NoteActions
            isEditable={isEditable}
            isEditing={isEditing}
            note={note}
            patientId={patientId}
            setDraftNoteId={setDraftNoteId}
            setIsEditing={setIsEditing}
            setShowDeleteNotePopUp={setShowDeleteNotePopUp}
          />
        </div>

        {(noteBadges || noteMeta) && (
          <>
            <Divider2 />

            {isRejected ? (
              <div className="tw:flex tw:flex-col tw:gap-4">
                {/* TODO: Confirm this combination is possible */}
                {noteBadges && (
                  <ul className="tw:flex tw:items-start tw:justify-start tw:gap-2 tw:lg:flex-row">
                    {noteBadges}
                  </ul>
                )}

                <CosignRejectedAlert noteId={noteId} />
              </div>
            ) : (
              <ul className="tw:flex tw:items-start tw:justify-start tw:gap-2 tw:lg:flex-row">
                {noteBadges}
                {noteMeta}
              </ul>
            )}
          </>
        )}
      </DashboardCard2>

      {patient && diagnosis && (
        <MemberHeader
          age={calculateAge(_.get(patient, "dob", ""))}
          className=""
          dayOfCare={_.get(patient, "number_of_days_in_care", "")}
          dob={_.get(patient, "dob")}
          memberName={`${_.get(patient, "first_name", "")} ${_.get(patient, "last_name", "")}`}
          phoneNumber={formatMobileNumber(_.get(patient, "mobile"))}
          primaryDiagnosis={primaryDiagnosis === "" ? "None" : primaryDiagnosis}
          secondaryDiagnosis={secondaryDiagnosis === "" ? "None" : secondaryDiagnosis}
        />
      )}

      <GenericNoteView
        isEditing={isEditing}
        note={note}
        patient={patient}
        patientName={patient && `${patient.first_name} ${patient.last_name}`}
        refetchNote={refetchNote}
        setIsEditing={setIsEditing}
      />

      {note && note.patient_id && <Addendums note={note} />}
      {note && note.patient_id && <CosignatureReviewSection note={note} />}
    </>
  );
}
